import { z } from 'zod';
import type { RuleEvaluationResult } from './rules';
import { basePatientSchema } from './basePatient';
import { providerSchema } from './provider';
import { baseHealthcardSchema } from './healthcard';
import { CLAIM_TARIFF_STATUS, tariffSchema } from './tariff';

export const hasOnlyIncompleteTariffs = (claim: unknown): boolean => {
  if (!claim || typeof claim !== 'object' || !('tariffs' in claim))
    return false;
  return (
    Array.isArray(claim.tariffs) &&
    claim.tariffs.length > 0 &&
    claim.tariffs.every(
      (tariff: unknown) =>
        tariff !== null &&
        typeof tariff === 'object' &&
        'status' in tariff &&
        tariff.status === CLAIM_TARIFF_STATUS.INCOMPLETE
    )
  );
};

const incompleteTariffSchema = tariffSchema.extend({
  status: z.literal(CLAIM_TARIFF_STATUS.INCOMPLETE),
  icd9: z.string().nullable().optional(),
  split: z.string().default(' '),
  incision: z.string().nullable().optional(),
  percentage: z.number().nullable().optional(),
  anesthesiaUnits: z.number().nullable().optional(),
});

// Keep persistence types and identifiers valid while allowing unfinished billing details.
const optionalSelectionID = z
  .union([z.guid(), z.literal('').transform(() => null)])
  .nullish();

export const incompleteClaimFields = {
  patientID: optionalSelectionID,
  providerID: optionalSelectionID,
  payorID: optionalSelectionID,
  healthcardID: optionalSelectionID,
  provider: z.lazy(() => providerSchema).nullish(),
  patient: z.lazy(() => basePatientSchema).nullish(),
  serviceStart: z
    .union([z.date(), z.iso.datetime({ offset: true }), z.iso.date()])
    .pipe(z.coerce.date()),
  serviceEnd: z.coerce.date().nullish(),
  chiroService: z.string().nullish(),
  interpretingRadiologist: z.string().nullish(),
  serviceLocationIndicator: z.string().nullish(),
  locationOfService: z.string().nullish(),
  virtualPhysicianRegion: z.string().nullish(),
  optometristReason: z.string().nullish(),
  thirdPartyLiability: z.string().nullish(),
  incorporated: z.string().nullish(),
  claimType: z.string().nullish(),
  claimMode: z.string().nullish(),
  formType: z.string().nullish(),
  corporationIndicator: z.string().nullish(),
  specialCircumstances: z.string().nullish(),
  confidentialCode: z.string().nullish(),
  hospitalCode: z.string().nullish(),
  facilityNumber: z.string().nullish(),
  patientCount: z.number().int().or(z.string().regex(/^\d+$/)).nullish(),
  healthcard: baseHealthcardSchema
    .extend({ patientID: z.guid(), province: z.string() })
    .nullish(),
  tariffs: z
    .array(incompleteTariffSchema)
    .min(1, 'Must have at least one tariff'),
};

export const withIncompleteClaimValidation = <
  Complete extends z.ZodType,
  Incomplete extends z.ZodType
>(
  completeSchema: Complete,
  incompleteSchema: Incomplete
) =>
  z
    .unknown()
    .transform((input, ctx): z.output<Complete> | z.output<Incomplete> => {
      const schema = hasOnlyIncompleteTariffs(input)
        ? incompleteSchema
        : completeSchema;
      const result = schema.safeParse(input);
      if (!result.success) {
        result.error.issues.forEach((issue) => ctx.addIssue({ ...issue }));
        return z.NEVER;
      }
      return result.data;
    });

export const getIncompleteClaimRulesResult = (): RuleEvaluationResult => ({
  passed: true,
  violations: [],
  appliedData: {},
  appliedActions: [],
});
